import {
    AdsClickOutlinedIcon,
    PeopleOutlineIcon,
    FeedOutlinedIcon,
    EmailOutlinedIcon,
    SearchOutlinedIcon,
    JoinInnerIcon,
    FolderSharedOutlinedIcon,
} from 'tuui';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { isVideoAccount } from '../../account-management';
import { isOpenExchangeAccount, isPmpAccount } from '../../account-management/accountType';
import { COMBINED_AUDIENCES_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/combined-audience/combinedAudiencesReportId';
import { CUSTOM_CONTEXTUAL_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/custom-contextual/customContextualReportId';
import { FIRST_PARTY_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/first-party/firstPartyReportId';
import { PREDICTIVE_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/lookalike/predictiveReportId';
import { MAIL_DOMAINS_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/mail-domains/mailDomainsReportId';
import { PIXEL_BASED_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/pixel-based/pixelBasedReportId';
import { SEARCH_KEYWORDS_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/search-keywords/searchReportsReportId';
import { isUserPermissionOrFeatureFlagEnabled } from './audienceCreatorConfigUtils';

const isSupportedAccountTypeForCombined = selectedAccount => {
    const { partnerTypes } = selectedAccount;

    return !isVideoAccount(partnerTypes) && !isPmpAccount(selectedAccount) && !isOpenExchangeAccount(selectedAccount);
};

const yahooAudiencesVisibilityCondition = ({ permissionsMap, accountConfig }) =>
    isUserPermissionOrFeatureFlagEnabled({
        permissionsMap,
        accountConfig,
        requiredPermission: 'YAHOO_SRT_MRT_AUDIENCE',
        featureFlagName: FEATURE_FLAGS.ADS_CONSOLE_YAHOO_AUDIENCES_ALLOWED,
    });

export const yourDataAudienceConfigs = [
    {
        id: PIXEL_BASED_REPORT_ID,
        label: 'Pixel',
        subtext: 'What they did on your site',
        IconComponent: AdsClickOutlinedIcon,
        tooltipText:
            'Convert visitor interactions on your website into audience segments. This option allows for highly personalized retargeting based on user actions throughout the funnel, from specific page visits to purchases, alongside suppressing users who have already converted.',
    },
    {
        id: PREDICTIVE_REPORT_ID,
        label: 'Predictive Audience',
        subtext: 'Similar users to existing audiences',
        IconComponent: PeopleOutlineIcon,
        tooltipText:
            'Expand your reach by targeting new users who share characteristics with your existing customers. This method leverages advanced algorithms to find new prospects likely to be interested in your offerings.',
        visibilityCondition: ({ isPbpAllowed, isCrmLookalikeAllowed }) => isPbpAllowed || isCrmLookalikeAllowed,
    },
    {
        id: FIRST_PARTY_REPORT_ID,
        label: 'Customer List',
        subtext: 'Data from your CRM',
        IconComponent: FolderSharedOutlinedIcon,
        tooltipText:
            'Segment audiences by customer behaviour or demographic based on your own CRM data. This is ideal for re-targeting specific groups within your existing customer base, such as loyal or churned customers, as well as for suppression purposes.',
        visibilityCondition: ({ permissionsMap, accountConfig }) =>
            isUserPermissionOrFeatureFlagEnabled({
                permissionsMap,
                accountConfig,
                requiredPermission: 'CREATE_CUSTOMER_FILE_ALLOWED',
                featureFlagName: FEATURE_FLAGS.CREATE_CUSTOMER_FILE_ALLOWED,
            }),
    },
];

export const taboolaDataAudienceConfigs = [
    {
        id: CUSTOM_CONTEXTUAL_REPORT_ID,
        label: 'Topics',
        subtext: 'What they currently reading',
        IconComponent: FeedOutlinedIcon,
        tooltipText:
            'Engage with users in real-time as they explore content related to specific topics. This dynamic targeting approach will allow you to place your message directly in the context of their current interests, enhancing relevance and engagement.',
        visibilityCondition: ({ permissionsMap, accountConfig }) =>
            isUserPermissionOrFeatureFlagEnabled({
                permissionsMap,
                accountConfig,
                requiredPermission: 'ADS_CONSOLE_CUSTOM_CONTEXTUAL',
                featureFlagName: FEATURE_FLAGS.ADS_CONSOLE_CUSTOM_CONTEXTUAL_ALLOWED,
            }),
    },
    {
        id: MAIL_DOMAINS_REPORT_ID,
        label: 'Email Domains',
        subtext: 'Who their emails are from',
        IconComponent: EmailOutlinedIcon,
        tooltipText:
            'Target users based on the domains of the emails they have received. This unique approach allows for precise targeting, especially useful for reaching audiences engaged with specific industries or competitors.',
        visibilityCondition: yahooAudiencesVisibilityCondition,
    },
    {
        id: SEARCH_KEYWORDS_REPORT_ID,
        label: 'Search Keywords',
        subtext: 'What they have searched',
        IconComponent: SearchOutlinedIcon,
        tooltipText:
            'Create segments based on the search terms users have entered. This option taps into user intent, enabling you to retarget people actively seeking information or products related to your business.',
        visibilityCondition: yahooAudiencesVisibilityCondition,
    },
];

export const moreAudienceConfigs = [
    {
        id: COMBINED_AUDIENCES_REPORT_ID,
        label: 'Combined Audiences',
        subtext: 'Built from existing audiences',
        IconComponent: JoinInnerIcon,
        tooltipText:
            'Enhance your targeting precision by combining multiple relevant audience segments together. This helps with greater organisation of audiences from different data sources and easy re-use for future campaigns.',
        visibilityCondition: ({ permissionsMap, accountConfig, selectedAccount }) => {
            const isSupportedAccountType = isSupportedAccountTypeForCombined(selectedAccount);
            const isAllowed = isUserPermissionOrFeatureFlagEnabled({
                permissionsMap,
                accountConfig,
                requiredPermission: 'ADS_CONSOLE_COMBINED_AUDIENCES',
                featureFlagName: FEATURE_FLAGS.ADS_CONSOLE_COMBINED_AUDIENCES_ALLOWED,
            });
            return isAllowed && isSupportedAccountType;
        },
    },
];
